import { isElement, isText } from './dom/node';
import { indexOf } from './iterable';
import { isSelectionPresent } from './selection';

enum BookmarkAttributeName {
  ANCHOR = 'data-editor-anchor',
  FOCUS = 'data-editor-focus',
}

export function isSelectionBookmarkAttributeName(attrName: string): boolean {
  return attrName === BookmarkAttributeName.ANCHOR || attrName === BookmarkAttributeName.FOCUS;
}

export function updateSelectionBookmarksInElement(element: Element) {
  removeSelectionBookmarksInElement(element);
  serializeSelectionBookmarksInElement(element);
}

export function serializeSelectionBookmarksInElement(element: Element) {
  const selection = element.ownerDocument && element.ownerDocument.getSelection();
  if (
    !isSelectionPresent(selection) ||
    !element.contains(selection.anchorNode) ||
    !element.contains(selection.focusNode)
  ) {
    return;
  }

  serializeSelectionBookmark(
    element,
    BookmarkAttributeName.ANCHOR,
    selection.anchorNode,
    selection.anchorOffset,
  );
  serializeSelectionBookmark(
    element,
    BookmarkAttributeName.FOCUS,
    selection.focusNode,
    selection.focusOffset,
  );
}

export function deserializeSelectionBookmarksInElement(element: Element) {
  const selection = element.ownerDocument && element.ownerDocument.getSelection();
  if (!selection) {
    return;
  }

  const anchor = deserializeSelectionBookmark(element, BookmarkAttributeName.ANCHOR);
  if (!anchor) {
    return;
  }

  if (anchor.node !== selection.anchorNode || anchor.offset !== selection.anchorOffset) {
    selection.collapse(anchor.node, anchor.offset);
  }

  const focus = deserializeSelectionBookmark(element, BookmarkAttributeName.FOCUS);
  if (!focus) {
    return;
  }

  if (focus.node !== selection.focusNode || focus.offset !== selection.focusOffset) {
    selection.extend(focus.node, focus.offset);
  }
}

export function removeSelectionBookmarksInElement(element: Element) {
  element
    .querySelectorAll(`[${BookmarkAttributeName.ANCHOR}], [${BookmarkAttributeName.FOCUS}]`)
    .forEach(removeSelectionBookmarkAttributesFromElement);
}

export function removeSelectionBookmarkAttributesFromElement(element: Element) {
  element.removeAttribute(BookmarkAttributeName.ANCHOR);
  element.removeAttribute(BookmarkAttributeName.FOCUS);
}

// TODO: Make this function more legible.
function serializeSelectionBookmark(
  element: Element,
  attrName: BookmarkAttributeName,
  node: Node,
  offset: number,
) {
  if (isElement(node)) {
    node.setAttribute(attrName, `${offset}`);
    return;
  }

  if (!node.parentNode || node.parentNode === element || !isElement(node.parentNode)) {
    return;
  }

  const parentElement = node.parentNode;
  const childIndex = indexOf(parentElement.childNodes, node);
  if (childIndex === -1) {
    return;
  }

  parentElement.setAttribute(attrName, `${childIndex}:${offset}`);
}

// TODO: Make this function more legible.
function deserializeSelectionBookmark(
  element: Element,
  attrName: BookmarkAttributeName,
): { node: Node; offset: number } | null {
  const bookmarkElement = element.querySelector(`[${attrName}]`);
  if (!bookmarkElement) {
    return null;
  }

  const value = bookmarkElement.getAttribute(attrName) || '';
  if (value.match(/^\d+$/)) {
    const offset = parseInt(value, 10);
    if (offset >= 0 && offset <= bookmarkElement.childNodes.length) {
      return { node: bookmarkElement, offset };
    }

    return null;
  }

  if (!value.match(/^\d+:\d+$/)) {
    return null;
  }

  const parts = value.split(':');
  const childIndex = parseInt(parts[0], 10);
  const offset = parseInt(parts[1], 10);

  const childNode = bookmarkElement.childNodes[childIndex];
  if (childNode && isText(childNode) && offset >= 0 && offset <= childNode.length) {
    return { node: childNode, offset };
  }

  return null;
}
